import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'data-weather',
    category: 'data',
    title: 'Soubory s meteorologickými daty',
    summary: 'Volitelné soubory, které zvyšují přesnost prognóz solární a větrné energie.',
    body: (
      <>
        <p>
          Soubory s meteorologickými daty jsou volitelné, ale zlepšují přesnost prognóz. Existují dva druhy.
        </p>
        <h5 className="mt-3">Solární meteorologická data</h5>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Sloupec</th><th>Povinný?</th><th>Význam</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>timestamp</Code></td><td>Povinný</td><td>Hodina</td></tr>
            <tr><td><Code>ghi_w_m2</Code></td><td>Povinný</td><td>Intenzita slunečního záření ve wattech na metr čtvereční</td></tr>
            <tr><td><Code>temperature_c</Code></td><td>Volitelný</td><td>Teplota vzduchu, °C</td></tr>
            <tr><td><Code>cloud_cover_pct</Code></td><td>Volitelný</td><td>Oblačnost, 0–100 %</td></tr>
            <tr><td><Code>dni_w_m2</Code></td><td>Volitelný</td><td>Přímé sluneční záření, W/m²</td></tr>
          </tbody>
        </Table>
        <TemplateBtn type="weather_solar">Šablona solárních meteorologických dat</TemplateBtn>

        <h5 className="mt-4">Větrná meteorologická data</h5>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Sloupec</th><th>Povinný?</th><th>Význam</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>timestamp</Code></td><td>Povinný</td><td>Hodina</td></tr>
            <tr><td><Code>wind_speed_m_s</Code></td><td>Povinný</td><td>Rychlost větru v metrech za sekundu</td></tr>
            <tr><td><Code>wind_direction_deg</Code></td><td>Volitelný</td><td>Směr větru, 0–360°</td></tr>
            <tr><td><Code>temperature_c</Code></td><td>Volitelný</td><td>Teplota vzduchu, °C</td></tr>
          </tbody>
        </Table>
        <TemplateBtn type="weather_wind">Šablona větrných meteorologických dat</TemplateBtn>
        <Tip>Volitelné sloupce lze zcela vynechat — pokud však sloupec zahrnete, vyplňte ho pro každý řádek.</Tip>

        <h5 className="mt-4">Kde získám meteorologická data?</h5>
        <p>
          Tato data téměř nikdy nepotřebujete měřit sami. Několik bezplatných služeb zveřejňuje hodinové
          historické záznamy počasí pro libovolné místo v Evropě. Začněte s univerzálními zdroji — pokrývají
          všechny pilotní země a nevyžadují žádný zvláštní účet.
        </p>

        <h6 className="mt-3">Open-Meteo (doporučeno, nejjednodušší)</h6>
        <p>
          <Ext href="https://open-meteo.com/en/docs/historical-weather-api">open-meteo.com/en/docs/historical-weather-api</Ext>
          {' '}— zdarma, bez registrace, hodinová historie od roku 1940.
        </p>
        <ol>
          <li>Otevřete výše uvedenou stránku. Do formuláře zadejte <strong>zeměpisnou šířku a
            délku</strong> vaší komunity (najdete je v Google Maps — klikněte pravým tlačítkem na místo → „Co je tady").</li>
          <li>Nastavte <strong>Datum zahájení</strong> a <strong>Datum ukončení</strong> pro požadovaný rok.</li>
          <li>V části <strong>Hodinové proměnné</strong> zaškrtněte níže uvedené položky (přeskočte seznam větru,
            pokud potřebujete pouze solární data, a naopak):
            <ul>
              <li><Code>shortwave_radiation</Code> — jedná se o GHI (ve W/m²).</li>
              <li><Code>direct_radiation</Code> — jedná se o DNI (volitelné).</li>
              <li><Code>temperature_2m</Code> — teplota vzduchu (°C).</li>
              <li><Code>cloud_cover</Code> — oblačnost (%).</li>
              <li><Code>wind_speed_10m</Code> — rychlost větru ve výšce 10 m (m/s).</li>
              <li><Code>wind_direction_10m</Code> — směr větru (stupně).</li>
            </ul>
          </li>
          <li>Nastavte formát na <strong>CSV</strong> a klikněte na <strong>Stáhnout</strong>.</li>
          <li>Otevřete soubor. Přejmenujte sloupce tak, jak je platforma očekává:
            {' '}<Code>shortwave_radiation</Code> → <Code>ghi_w_m2</Code>,
            {' '}<Code>direct_radiation</Code> → <Code>dni_w_m2</Code>,
            {' '}<Code>temperature_2m</Code> → <Code>temperature_c</Code>,
            {' '}<Code>cloud_cover</Code> → <Code>cloud_cover_pct</Code>,
            {' '}<Code>wind_speed_10m</Code> → <Code>wind_speed_m_s</Code>,
            {' '}<Code>wind_direction_10m</Code> → <Code>wind_direction_deg</Code>.</li>
          <li>Ujistěte se, že sloupec s časovým razítkem má název <Code>timestamp</Code>. Uložte jako CSV UTF-8 a
            nahrajte.</li>
        </ol>

        <h6 className="mt-3">PVGIS — Evropská komise JRC (nejlepší pro plánování solárních systémů)</h6>
        <p>
          <Ext href="https://re.jrc.ec.europa.eu/pvg_tools/en/">re.jrc.ec.europa.eu/pvg_tools/en/</Ext>
          {' '}— zdarma, bez registrace, pokrývá celou Evropu. Zvláště užitečné, pokud potřebujete referenci
          „typického roku" (TMY) místo konkrétního historického roku.
        </p>
        <ol>
          <li>Otevřete stránku, vyberte svou polohu na mapě.</li>
          <li>V levém panelu záložek zvolte <strong>„Hodinová data"</strong>.</li>
          <li>Zaškrtněte <strong>Složky solárního záření</strong> a <strong>Teplota vzduchu ve 2 m</strong>,
            vyberte rok (nebo rozmezí) a klikněte na <strong>Stáhnout CSV</strong>.</li>
          <li>Stažený soubor obsahuje malou hlavičku, kterou může být nutné odstranit, aby řádek se sloupci
            (<Code>time</Code>, <Code>G(i)</Code>, <Code>T2m</Code>, …) byl na řádku 1. Přejmenujte
            {' '}<Code>G(i)</Code> → <Code>ghi_w_m2</Code> a <Code>T2m</Code> → <Code>temperature_c</Code>.</li>
        </ol>
        <Tip>Časy v PVGIS jsou obvykle v UTC a časová razítka mohou vypadat jako <Code>20250101:1000</Code>
          — převeďte je na formát <Code>2025-01-01T10:00:00Z</Code> pomocí vzorce z článku
          {' '}<ArticleLink to="data-csv-basics">Správné vytváření souborů CSV</ArticleLink>.</Tip>

        <h6 className="mt-3">Renewables.ninja (solární / větrná výroba pro konkrétní lokalitu)</h6>
        <p>
          <Ext href="https://www.renewables.ninja">renewables.ninja</Ext> — zdarma po rychlé registraci.
          Zadejte souřadnice, zvolte „MERRA-2" nebo „ERA-5", rok a rozlišení a stáhněte CSV. Proměnná
          k namapování je <Code>irradiance</Code> → <Code>ghi_w_m2</Code> pro solární data
          a <Code>wind_speed</Code> → <Code>wind_speed_m_s</Code> pro větrná data.
        </p>

        <h6 className="mt-3">NASA POWER (globální záložní zdroj)</h6>
        <p>
          <Ext href="https://power.larc.nasa.gov/data-access-viewer/">power.larc.nasa.gov/data-access-viewer</Ext>
          {' '}— globální hodinová data, zdarma. Užitečné, když ostatní zdroje danou lokalitu nepokrývají.
          Názvy proměnných: <Code>ALLSKY_SFC_SW_DWN</Code> → <Code>ghi_w_m2</Code>,
          {' '}<Code>T2M</Code> → <Code>temperature_c</Code>,
          {' '}<Code>WS10M</Code> → <Code>wind_speed_m_s</Code>.
        </p>

        <h6 className="mt-3">Národní meteorologické služby</h6>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Země</th><th>Služba</th></tr>
          </thead>
          <tbody>
            <tr><td>Itálie</td><td>ISPRA / regionální ARPA (např. ARPA Lombardia, ARPA Veneto) — hodinová data ze stanic podle regionů.</td></tr>
            <tr><td>Francie</td><td>Portál otevřených dat Météo-France (<Ext href="https://meteo.data.gouv.fr">meteo.data.gouv.fr</Ext>) — historická hodinová pozorování.</td></tr>
            <tr><td>Německo</td><td>DWD Climate Data Center (<Ext href="https://opendata.dwd.de">opendata.dwd.de</Ext>) — zdarma, hodinová data ze stanic.</td></tr>
            <tr><td>Česká rep.</td><td>CHMI (<Ext href="https://www.chmi.cz">chmi.cz</Ext>) — historická data ze stanic na vyžádání / přes portál.</td></tr>
            <tr><td>Řecko</td><td>HNMS / NOA-Meteo (<Ext href="https://www.meteo.gr">meteo.gr</Ext>) — omezená bezplatná historická data; Open-Meteo bývá obvykle jednodušší.</td></tr>
          </tbody>
        </Table>

        <Warn>
          Slaďte <strong>rok</strong> a <strong>časové pásmo</strong> vašeho souboru s meteorologickými daty
          s ostatními soubory. Pokud jsou vaše data o spotřebě za rok 2024 v místním čase, stáhněte meteorologická
          data za rok 2024 v UTC a převeďte je (Open-Meteo umožňuje vybrat časové pásmo před stažením). Nesoulad
          let nebo časových pásem prognózy zhoršuje, nikoli zlepšuje.
        </Warn>
      </>
    ),
  },

  {
    id: 'data-bill',
    category: 'data',
    title: 'Soubory s fakturami za energii',
    summary: 'Měsíční fakturační data používaná pro analýzu nákladů.',
    body: (
      <>
        <p>
          Soubor s <strong>fakturou za energii</strong> obsahuje seznam minulých faktur, jeden řádek na
          zúčtovací období (zpravidla jeden měsíc). Používá se pro analýzu nákladů.
        </p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Sloupec</th><th>Povinný?</th><th>Význam</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>period_start</Code></td><td>Povinný</td><td>První den zúčtovacího období, např. <Code>2025-01-01</Code></td></tr>
            <tr><td><Code>period_end</Code></td><td>Povinný</td><td>Poslední den zúčtovacího období, např. <Code>2025-01-31</Code></td></tr>
            <tr><td><Code>consumption_kwh</Code></td><td>Povinný</td><td>Celková spotřeba elektřiny v daném období v kWh</td></tr>
            <tr><td><Code>cost_eur</Code></td><td>Povinný</td><td>Celková zaplacená částka v eurech</td></tr>
            <tr><td><Code>peak_demand_kw</Code></td><td>Volitelný</td><td>Nejvyšší příkon v daném období v kW</td></tr>
            <tr><td><Code>rate_eur_per_kwh</Code></td><td>Volitelný</td><td>Průměrná cena za kWh</td></tr>
          </tbody>
        </Table>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`period_start,period_end,consumption_kwh,cost_eur
2025-01-01,2025-01-31,3850,962.50
2025-02-01,2025-02-28,3520,880.00`}
        </pre>
        <TemplateBtn type="building_energy_bill">Stáhnout šablonu faktury za energii</TemplateBtn>
        <Warn>Datum zahájení každého období musí předcházet datu ukončení a období se nesmějí překrývat.</Warn>
      </>
    ),
  },
]
